import {FormattedMessage, useIntl} from 'react-intl';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom';

import {
  addReferrals,
  cmServiceAsyncActions,
  getCmService,
  getTranslations, referralsAsyncActions, removeReferrals
} from 'redux/pageAsyncActions';
import {IStore} from 'redux/interface';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';
import NotFoundInfo from 'components/not-found-info';
import Loader from 'components/loader';

import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import CrossroadImg from 'assets/image/library/skill-crossroad.svg';
import {useLoadFile} from '../../utils/hooks/useLoadFile';
import {ICmLocation} from '../../redux/cm-service/interfaces';

import './styles.scss';

const uniqById = <T extends { id: string | number }>(
  items: ReadonlyArray<T>
): T[] => items.reduce<{ seen: Record<string, true>; result: T[] }>(
  (acc, item) => {
    const key = String(item.id);

    if (!acc.seen[key]) {
      acc.seen[key] = true;
      acc.result.push(item);
    }

    return acc;
  },
  {seen: Object.create(null), result: []}
).result;

const normalizeListString = (value?: string) => {
  if (!value) return '';
  const parts = value
    .split(/[,\s]+/)
    .map(s => s.trim())
    .filter(Boolean);

  return parts.join(', ');
};

const formatCounties = (locations?: ICmLocation[]) => {
  if (!locations?.length) return '';

  return [...new Set(locations.map(l => l.county).filter(Boolean) as string[])]
    .sort()
    .join(', ');
};

export const CmResourceCard: React.FunctionComponent = () => {
  const {
    service
  } = useSelector((state: IStore) => state.cm_service);
  const {
    loading,
    errors
  } = useSelector((state: IStore) => state.status);
  const {
    userProfile, settings
  } = useSelector((state: IStore) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const {srvId} = useParams();
  const [searchParams] = useSearchParams();
  const turnOffCounter = searchParams.get('turnOffCounter');
  const [isAddedToMaterials, setIsAddedToMaterials] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const {
    getFileUrl
  } = useLoadFile();

  const resourcePage = useMemo(() => {
    return settings?.pages.find((p) => p.name.toLowerCase() === Pages.DmResource.toLowerCase())
    || settings?.pages.find((p) => p.name.toLowerCase() === Pages.CmResource.toLowerCase());
  }, [settings]);

  const translationKey = useMemo(() => {
    return resourcePage?.translationKey || TranslationKeys.CmResource;
  }, [settings]);

  useGetTranslations({translationKey});

  useEffect(() => {
    if (srvId) {
      dispatch(cmServiceAsyncActions[getCmService](Number(srvId), turnOffCounter === 'true'));
    }
  }, [srvId]);

  useEffect(() => {
    if (service) {
      setIsAddedToMaterials(service.material);
      if (!service.srvImageUrl && service.srvImageId) {
        getFileUrl(service.srvImageId, (url) => {
          if (url) {
            setImageUrl(url);
          }
        });
      } else {
        setImageUrl(service.srvImageUrl);
      }
    }
  }, [service]);

  const locations = useMemo(
    () => (service?.locations?.length ? uniqById(service.locations) : []),
    [service?.locations]
  );

  const counties = useMemo(() => formatCounties(service?.locations), [service?.locations]);

  const languages = useMemo(() => normalizeListString(service?.languages), [service?.languages]);

  const ages = useMemo(() => normalizeListString(service?.ages), [service?.ages]);

  const typeTreatments = useMemo(
    () => (service?.typeTreatments ? service.typeTreatments.split(/,+/)
      .map(s => s.trim()).filter(Boolean).join(', ') : ''),
    [service?.typeTreatments]
  );

  const mcalInsurances = useMemo(
    () => (service?.mcalInsurances ? service.mcalInsurances.split(/,+/)
      .map(s => s.trim())
      .filter(Boolean)
      .sort()
      .join(', ') : ''),
    [service?.mcalInsurances]
  );

  const insurances = useMemo(
    () => (service?.insurances ? service.insurances.split(/,+/)
      .map(s => s.trim())
      .filter(Boolean)
      .sort()
      .join(', ') : ''),
    [service?.insurances]
  );

  const referralsRole = useMemo(() => {
    const page = settings?.pages.find(p => p.name.toLowerCase() === Pages.Referrals.toLowerCase());

    return page?.role ?? null;
  }, [settings]);

  const showReferralsButton = useMemo(() => {
    const page = settings?.pages.find(p => p.name.toLowerCase() === Pages.Referrals.toLowerCase());

    return Boolean(page);
  }, [settings]);

  const canSeeReferrals = showReferralsButton && (!referralsRole || userProfile?.roles?.includes(referralsRole));

  function handleAddToReferrals() {
    if (service) {
      if (service.material) {
        dispatch(referralsAsyncActions[removeReferrals]([service.srvId], false));
      } else {
        dispatch(referralsAsyncActions[addReferrals](service.srvId));
      }
      setIsAddedToMaterials(!isAddedToMaterials);
    }
  }

  const handleOpenServiceOrg = useCallback(() => {
    if (!service?.orgId) {
      return;
    }

    navigate(`/cmresource/org/${service.orgId}`);
  }, [navigate, service?.orgId]);

  const isLoading = loading.includes(getCmService);
  const errorMsg = errors[getCmService]?.error?.message ?? '';
  const isNotFound = errorMsg.includes('500') || errorMsg.includes('400');

  const renderBreadcrumbs = () => (
    <div className="breadcrumbs">
      {resourcePage && resourcePage.name.toLowerCase() === Pages.CmResource.toLowerCase() && <Link className="breadcrumbs__previous" to="/cmresource">
        <FormattedMessage id="gritx.cmresource.breadcrumb.previous" defaultMessage="Resources"/>
      </Link>}
      {resourcePage && resourcePage.name.toLowerCase() === Pages.DmResource.toLowerCase() && <Link className="breadcrumbs__previous" to="/dmresource">
        <FormattedMessage id="gritx.cmresource.breadcrumb.previous" defaultMessage="Resources"/>
      </Link>}
    </div>
  );

  const renderActions = () => (
    <>
      {canSeeReferrals && (
        <GritxButton
          title={
            isAddedToMaterials
              ? intl.formatMessage({id: 'gritx.cmresource.card.saved', defaultMessage: 'Saved'})
              : intl.formatMessage({id: 'gritx.cmresource.card.addToReferrals', defaultMessage: 'Add to Referrals'})
          }
          variant={ButtonVariant.Primary}
          onClick={handleAddToReferrals}
        />
      )}
    </>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="cm-resource-card__view">
          <div className="cm-resource-card__data"><Loader nested /></div>
        </div>
      );
    }

    if (isNotFound) {
      return (
        <div className="cm-resource-card__view">
          <div className="cm-resource-card__data">
            <NotFoundInfo
              title={intl.formatMessage({
                id: 'gritx.cmresource.notFoundCard',
                defaultMessage: 'Sorry, we can\'t find the resource you are looking for.'
              })}
              image={CrossroadImg}
            />
          </div>
        </div>
      );
    }

    if (!service) return null;

    return (
      <div className="cm-resource-card__view">
        <div className="cm-resource-card__data">
          <div className="cm-resource-card__title">
            <h2 className="cm-resource-card__heading">{service.srvName}</h2>
          </div>

          <div className="cm-resource-card__props">
            <div className="cm-resource-card__actions">{renderActions()}</div>
          </div>

          <div className="cm-resource-card__body">
            <p className="cm-resource-card__description">
              {service.orgId && (
                <button className="cm-resource-card__org_button" onClick={handleOpenServiceOrg}>
                  <FormattedMessage id="gritx.cmresource.card.orgBy" defaultMessage="By" />
                  {': '}
                  {service.orgName}
                </button>
              )}
            </p>

            {imageUrl && (
              <div className="cm-resource-card__image-wrapper">
                <img src={imageUrl} alt="" className="cm-resource-card__image" />
              </div>
            )}

            <div className="cm-resource-card__panel-content">
              <div className="cm-resource-card__left-column">
                <section className="cm-resource-card__section">
                  <h2><FormattedMessage id="gritx.cmresource.card.mainServices" defaultMessage="Main services" />:</h2>
                  <p>{typeTreatments}</p>
                </section>

                {!!locations.length && (
                  <section className="cm-resource-card__section">
                    <h2><FormattedMessage id="gritx.cmresource.card.contacts" defaultMessage="Contacts" />:</h2>
                    {locations.map(location => (
                      <div key={location.id} className="cm-resource-card__section">
                        {location.city && (
                          <p><strong><FormattedMessage id="gritx.cmresource.card.city" defaultMessage="City" />: </strong>{location.city}</p>
                        )}
                        {location.address && (
                          <p><strong><FormattedMessage id="gritx.cmresource.card.address" defaultMessage="Address" />: </strong>{location.address}</p>
                        )}
                        {location.phone && (
                          <p><strong><FormattedMessage id="gritx.cmresource.card.phone" defaultMessage="Phone" />: </strong>{location.phone}</p>
                        )}
                        {location.website && (
                          <p>
                            <strong>Web: </strong>
                            <a href={location.website} target="_blank" rel="noreferrer">{location.website}</a>
                          </p>
                        )}
                      </div>
                    ))}
                  </section>
                )}

                <section className="cm-resource-card__section">
                  <h2><FormattedMessage id="gritx.cmresource.card.process" defaultMessage="Process" />:</h2>
                  <p>{service.process}</p>
                </section>

                <section className="cm-resource-card__section">
                  <h2><FormattedMessage id="gritx.cmresource.card.notes" defaultMessage="Description" />: </h2>
                  <p>{service.treatmentDescription}</p>
                </section>
              </div>

              <div className="cm-resource-card__right-column">
                <section className="cm-resource-card__section">
                  <h3><FormattedMessage id="gritx.cmresource.card.counties" defaultMessage="Counties Served" />:</h3>
                  <p>{counties}</p>
                </section>
                <section className="cm-resource-card__section">
                  <h3><FormattedMessage id="gritx.cmresource.card.serving" defaultMessage="Serving" />:</h3>
                  <p>{ages}</p>
                </section>
                <section className="cm-resource-card__section">
                  <h3><FormattedMessage id="gritx.cmresource.card.languages" defaultMessage="Languages" />:</h3>
                  <p>{languages}</p>
                </section>

                {service.mcalAccepts && <section className="cm-resource-card__section">
                  <h2><FormattedMessage id="gritx.cmresource.card.mcal.accepted"
                    defaultMessage="Medi-Cal is accepted"/></h2>
                  <p>{mcalInsurances}</p>
                  {service.mcalAcceptsNotes && <div className="cm-resource-card__section-note">{service.mcalAcceptsNotes}</div>}
                </section>}

                {service.insuranceAccepts && <section className="cm-resource-card__section">
                  <h2><FormattedMessage id="gritx.cmresource.card.commercial.accepted"
                    defaultMessage="Commercial insurance is accepted"/></h2>
                  <p>{insurances}</p>
                  {service.insuranceAcceptsNotes && <div className="cm-resource-card__section-note">{service.insuranceAcceptsNotes}</div>}
                </section>}

                {service.cashOnly && <section className="cm-resource-card__section">
                  <h2><FormattedMessage id="gritx.cmresource.card.privatePay"
                    defaultMessage="Private Pay"/></h2>
                  <p>{service.cashOnlyNotes}</p>
                </section>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="cm-resource-card">
      {!loading.includes(getTranslations) && (
        <>
          {renderBreadcrumbs()}
          <div className="cm-resource-card__wrapper">{renderContent()}</div>
        </>
      )}
    </div>
  );
};
